/**
 * Admin Analytics Page
 * Shows user usage statistics with visualizations
 */

import { useState, useEffect } from 'react';
import { adminService, UserUsageStats } from '../../services/admin.service';

export function AnalyticsPage() {
  const [users, setUsers] = useState<UserUsageStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'totalStorageBytes' | 'fileCount'>('totalStorageBytes');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [storageBreakdown, setStorageBreakdown] = useState<any>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersData, storageData] = await Promise.all([
        adminService.getUserUsageStats({ sortBy, sortOrder, limit: 50 }),
        adminService.getStorageBreakdown(),
      ]);

      setUsers(usersData.items);
      setStorageBreakdown(storageData);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Analytics</h1>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      ) : error ? (
        <div className="text-red-500 text-center py-8">{error}</div>
      ) : (
        <div className="space-y-6">
          {/* Storage Breakdown */}
          {storageBreakdown?.total && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Storage Overview
              </h2>
              <div className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {storageBreakdown.total?.bytesFormatted ?? '0 B'}
              </div>
              <div className="flex gap-4 mb-4">
                {/* Storage bar */}
                <div className="flex-1 h-8 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
                  <div
                    className="bg-blue-500 h-full"
                    style={{
                      width: `${storageBreakdown.breakdown?.documents?.percentage ?? 0}%`,
                    }}
                    title={`Documents: ${storageBreakdown.breakdown?.documents?.percentage ?? 0}%`}
                  />
                  <div
                    className="bg-green-500 h-full"
                    style={{ width: `${storageBreakdown.breakdown?.images?.percentage ?? 0}%` }}
                    title={`Images: ${storageBreakdown.breakdown?.images?.percentage ?? 0}%`}
                  />
                  <div
                    className="bg-purple-500 h-full"
                    style={{ width: `${storageBreakdown.breakdown?.videos?.percentage ?? 0}%` }}
                    title={`Videos: ${storageBreakdown.breakdown?.videos?.percentage ?? 0}%`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-gray-600 dark:text-gray-400">Documents:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {storageBreakdown.breakdown?.documents?.count ?? 0} (
                    {storageBreakdown.breakdown?.documents?.bytesFormatted ?? '0 B'})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-gray-600 dark:text-gray-400">Images:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {storageBreakdown.breakdown?.images?.count ?? 0} (
                    {storageBreakdown.breakdown?.images?.bytesFormatted ?? '0 B'})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="text-gray-600 dark:text-gray-400">Videos:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {storageBreakdown.breakdown?.videos?.count ?? 0} (
                    {storageBreakdown.breakdown?.videos?.bytesFormatted ?? '0 B'})
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* User Usage Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                User Storage Usage
              </h2>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'totalStorageBytes' | 'fileCount')}
                  className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                >
                  <option value="totalStorageBytes">By Size</option>
                  <option value="fileCount">By Files</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                >
                  {sortOrder === 'desc' ? '↓' : '↑'}
                </button>
              </div>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    User
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Files
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Storage
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Last Upload
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {user.displayName || user.email}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                      {user.fileCount}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-white">
                      {formatBytes(user.totalStorageBytes)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 text-sm">
                      {user.lastUploadAt ? new Date(user.lastUploadAt).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No user storage data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalyticsPage;
